import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api/errors";
import { useDeleteSecret, useSecrets } from "../../api/queries";
import type { SecretSummary } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useFleet } from "../../fleet/FleetContext";
import { useToast } from "../../toast/ToastContext";
import { CreateSecretModal } from "./CreateSecretModal";
import { DeleteSecretDialog } from "./DeleteSecretDialog";
import { RotateSecretModal } from "./RotateSecretModal";
import "./secrets.css";

const WRITE_ONLY_NOTE =
  "Secret values are write-only — encrypted at rest and never shown again. Rotate to replace.";

export function SecretsPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch } = useSecrets(currentId, { cursor: paginator.cursor });
  const deleteMutation = useDeleteSecret(currentId);

  const [createOpen, setCreateOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<SecretSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SecretSummary | null>(null);

  // Deny-by-default mirrors the server's authz matrix (server/internal/authz):
  // every role but viewer can set/rotate/delete secrets. This only ever hides
  // or disables the affected controls — the server enforces the real decision
  // on every request regardless of what this check says (see the onError
  // handlers below for the 403 a stale/incorrect client-side guess would hit).
  const canManage = session?.role !== "viewer";

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id: secretId, name: secretName } = deleteTarget;
    deleteMutation.mutate(
      { secretId },
      {
        onSuccess: () => {
          pushToast({ title: `Deleted "${secretName}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't delete "${secretName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const newSecretButton = (
    <Button variant="primary" onClick={() => setCreateOpen(true)}>
      New secret
    </Button>
  );

  const columns: DataTableColumn<SecretSummary>[] = [
    { key: "name", header: "Name", cell: (secret) => secret.name, className: "data-table__primary" },
    { key: "version", header: "Version", cell: (secret) => `v${secret.version}`, className: "data-table__mono" },
    {
      key: "createdBy",
      header: "Created by",
      cell: (secret) => secret.createdBy || "—",
      className: "data-table__secondary",
    },
    {
      key: "updatedAt",
      header: "Updated",
      cell: (secret) => new Date(secret.updatedAt).toLocaleString(),
      className: "data-table__secondary",
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (secret: SecretSummary) => (
              <div className="secrets-table__actions">
                <Button variant="secondary" onClick={() => setRotateTarget(secret)}>
                  Rotate
                </Button>
                <Button variant="danger" onClick={() => setDeleteTarget(secret)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Secrets"
        description="Registry credentials, Git tokens, and other values your workloads need — scoped to this fleet."
        actions={canManage ? newSecretButton : undefined}
      />

      <p className="secrets-note">{WRITE_ONLY_NOTE}</p>

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading secrets…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load secrets. Check your connection and try again."
        }
        renderError={(err) =>
          err instanceof ApiError && err.code === "not_implemented" ? (
            <EmptyState
              title="Secrets aren't available yet"
              description="The secret store lands in a later pass of the control plane API. Nothing to configure here yet."
            />
          ) : undefined
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No secrets stored yet"
            description="Create one to make it available to workloads in this fleet. The value is sealed on write and never shown again."
            action={canManage ? newSecretButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(secret) => secret.id} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="secrets"
            />
          </>
        )}
      </QueryStates>

      {canManage && <CreateSecretModal open={createOpen} fleetId={currentId} onClose={() => setCreateOpen(false)} />}

      {canManage && (
        <RotateSecretModal secret={rotateTarget} fleetId={currentId} onClose={() => setRotateTarget(null)} />
      )}

      {canManage && (
        <DeleteSecretDialog
          secret={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
